import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  BarChart3, Plus, Download, Check, Lock, FileText, 
  TrendingUp, TrendingDown, DollarSign, Calendar
} from 'lucide-react';

const Balancetes = () => {
  const { user } = useAuth();
  const { getCondominioId } = useCondominio();
  const [balancetes, setBalancetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [balanceteDetalhado, setBalanceteDetalhado] = useState(null);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [detalhesMap, setDetalhesMap] = useState({});

  useEffect(() => {
    const condId = getCondominioId();
    if (condId) {
      loadBalancetes();
    }
  }, [anoSelecionado, getCondominioId()]);

  const loadBalancetes = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      
      if (!condominioId) {
        console.log('Condomínio não selecionado');
        setBalancetes([]);
        setLoading(false);
        return;
      }
      
      console.log('📊 Carregando balancetes do condomínio:', condominioId);
      
      const response = await api.get('/balancetes', {
        params: {
          condominio_id: condominioId,
          ano: anoSelecionado
        }
      });
      
      const lista = response.data.data.balancetes || [];
      console.log('📊 Balancetes recebidos:', lista.length);
      setBalancetes(lista);
      await carregarDetalhesBalancetes(lista);
    } catch (error) {
      console.error('Erro ao carregar balancetes:', error);
      setBalancetes([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarDetalhesBalancetes = async (listaBalancetes) => {
    if (!listaBalancetes || listaBalancetes.length === 0) {
      setDetalhesMap({});
      return;
    }

    try {
      const condominioId = getCondominioId();
      const resultados = await Promise.all(
        listaBalancetes.map(async (balancete) => {
          try {
            const response = await api.get(`/balancetes/${balancete.balancete_id}/detalhado`, {
              params: { condominio_id: condominioId }
            });

            const info = response.data?.data;
            if (!info) return [balancete.balancete_id, null];

            const detalhesNormalizados = normalizarDetalhes(balancete, info);
            return [balancete.balancete_id, detalhesNormalizados];
          } catch (error) {
            console.error('Erro ao carregar detalhes do balancete', balancete.balancete_id, error?.response?.data || error.message);
            return [balancete.balancete_id, null];
          }
        })
      );

      const mapa = resultados.reduce((acc, [id, detalhes]) => {
        if (detalhes) {
          acc[id] = detalhes;
        }
        return acc;
      }, {});

      setDetalhesMap(mapa);
    } catch (error) {
      console.error('Erro ao compilar detalhes dos balancetes:', error);
      setDetalhesMap({});
    }
  };

  const gerarBalancete = async (mes, ano) => {
    try {
      const response = await api.post('/balancetes/gerar', {
        condominio_id: getCondominioId(),
        mes,
        ano
      });
      
      loadBalancetes();
      
      if (window.showNotification) {
        window.showNotification('Balancete gerado com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Erro ao gerar balancete:', error);
      if (window.showNotification) {
        window.showNotification(error.response?.data?.message || 'Erro ao gerar balancete', 'error');
      }
    }
  };

  const fecharBalancete = async (id) => {
    try {
      await api.put(`/balancetes/${id}/fechar`);
      loadBalancetes();
      if (window.showNotification) {
        window.showNotification('Balancete fechado com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Erro ao fechar balancete:', error);
    }
  };

  const aprovarBalancete = async (id) => {
    try {
      await api.put(`/balancetes/${id}/aprovar`);
      loadBalancetes();
      if (window.showNotification) {
        window.showNotification('Balancete aprovado com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Erro ao aprovar balancete:', error);
    }
  };

  const verDetalhes = async (balancete) => {
    setBalanceteDetalhado(balancete);
    setShowDetalhes(true);
  };

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const parseValor = (valor) => {
    if (valor === null || valor === undefined || valor === '') return 0;
    if (typeof valor === 'number') return valor;

    const cleaned = String(valor)
      .replace(/R\$/g, '')
      .replace(/\u0000/g, '')
      .trim();

    if (cleaned === '') return 0;

    if (cleaned.includes(',')) {
      const normalized = cleaned.replace(/\./g, '').replace(',', '.');
      const parsed = parseFloat(normalized);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    const parsed = parseFloat(cleaned);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatCurrency = (value) => {
    const numeric = parseValor(value);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numeric);
  };

  const normalizarDetalhes = (balancete, info) => {
    const receitasTotal = parseValor(
      info.creditos?.total ??
      balancete?.balancete_total_receitas ??
      balancete?.total_receitas ??
      0
    );

    const despesasTotal = parseValor(
      info.totalDespesas ??
      balancete?.balancete_total_despesas ??
      balancete?.total_despesas ??
      0
    );

    const saldoTotal = parseValor(
      info.resumo?.saldoMesAtual ??
      balancete?.balancete_saldo ??
      balancete?.saldo ??
      (receitasTotal - despesasTotal)
    );

    return {
      receitas: receitasTotal,
      despesas: despesasTotal,
      saldo: saldoTotal,
      saldoAnterior: parseValor(info.saldoMesAnterior ?? balancete?.balancete_saldo_anterior ?? 0),
      inadimplenciaTotal: parseValor(info.inadimplenciaPeriodo?.total_numerico ?? info.inadimplenciaPeriodo?.total ?? 0),
      inadimplenciaQuantidade: info.inadimplenciaPeriodo?.quantidade ?? 0,
      acordosTotal: parseValor(info.acordosPeriodo?.total_numerico ?? info.acordosPeriodo?.total ?? 0),
      acordosQuantidade: info.acordosPeriodo?.quantidade ?? 0,
      statusInfo: info.statusInfo || null
    };
  };

  const getBalanceteMes = (mes) => {
    return balancetes.find(b => b.balancete_mes === mes);
  };

  const totalReceitasBalancete = (balancete) => {
    const detalhesResumo = detalhesMap[balancete?.balancete_id];
    if (detalhesResumo?.receitas !== undefined) {
      return detalhesResumo.receitas;
    }

    if (!balancete) return 0;
    const valorDireto = parseValor(balancete?.balancete_total_receitas ?? balancete?.total_receitas);
    if (valorDireto !== 0) return valorDireto;

    let detalhesPersistidos = balancete?.balancete_detalhes || balancete?.detalhes;
    if (typeof detalhesPersistidos === 'string') {
      try {
        detalhesPersistidos = JSON.parse(detalhesPersistidos);
      } catch (error) {
        detalhesPersistidos = null;
      }
    }

    const receitasDetalhes = detalhesPersistidos?.receitas;
    if (receitasDetalhes) {
      return Object.values(receitasDetalhes).reduce((acc, valor) => acc + parseValor(valor), 0);
    }
    return 0;
  };

  const totalDespesasBalancete = (balancete) => {
    const detalhesResumo = detalhesMap[balancete?.balancete_id];
    if (detalhesResumo?.despesas !== undefined) {
      return detalhesResumo.despesas;
    }

    if (!balancete) return 0;
    const valorDireto = parseValor(balancete?.balancete_total_despesas ?? balancete?.total_despesas);
    if (valorDireto !== 0) return valorDireto;

    let detalhesPersistidos = balancete?.balancete_detalhes || balancete?.detalhes;
    if (typeof detalhesPersistidos === 'string') {
      try {
        detalhesPersistidos = JSON.parse(detalhesPersistidos);
      } catch (error) {
        detalhesPersistidos = null;
      }
    }

    const despesasDetalhes = detalhesPersistidos?.despesas;
    if (despesasDetalhes) {
      return Object.values(despesasDetalhes).reduce((acc, valor) => acc + parseValor(valor), 0);
    }
    return 0;
  };

  const saldoBalancete = (balancete) => {
    const detalhesResumo = detalhesMap[balancete?.balancete_id];
    if (detalhesResumo?.saldo !== undefined) {
      return detalhesResumo.saldo;
    }

    const saldoDireto = parseValor(balancete?.balancete_saldo ?? balancete?.saldo);
    if (saldoDireto !== 0) return saldoDireto;

    return totalReceitasBalancete(balancete) - totalDespesasBalancete(balancete);
  };

  const getTotaisAno = () => {
    const totais = balancetes.reduce((acc, b) => ({
      receitas: acc.receitas + totalReceitasBalancete(b),
      despesas: acc.despesas + totalDespesasBalancete(b)
    }), { receitas: 0, despesas: 0 });

    return {
      ...totais,
      saldo: totais.receitas - totais.despesas
    };
  };

  const totaisAno = getTotaisAno();

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Balancetes</h1>
            <p className="text-sm text-gray-500">Relatórios Mensais e Anuais</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={anoSelecionado}
            onChange={(e) => setAnoSelecionado(parseInt(e.target.value))}
            className="form-select"
          >
            {[...Array(5)].map((_, i) => {
              const ano = new Date().getFullYear() - i;
              return <option key={ano} value={ano}>{ano}</option>;
            })}
          </select>
        </div>
      </div>

      {/* Resumo Anual */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Total Receitas {anoSelecionado}</p>
              <p className="text-2xl font-bold text-green-700">
                {formatCurrency(totaisAno.receitas)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Total Despesas {anoSelecionado}</p>
              <p className="text-2xl font-bold text-red-700">
                {formatCurrency(totaisAno.despesas)}
              </p>
            </div>
            <TrendingDown className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className={`${totaisAno.saldo >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'} p-4 rounded-lg border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${totaisAno.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                Saldo {anoSelecionado}
              </p>
              <p className={`text-2xl font-bold ${totaisAno.saldo >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                {formatCurrency(totaisAno.saldo)}
              </p>
            </div>
            <DollarSign className={`h-8 w-8 ${totaisAno.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
          </div>
        </div>
      </div>

      {/* Grid de Meses */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {meses.map((mes, index) => {
          const mesNum = index + 1;
          const balancete = getBalanceteMes(mesNum);
          const mesAtual = new Date().getMonth() + 1;
          const anoAtual = new Date().getFullYear();
          const mesPassado = anoSelecionado < anoAtual || (anoSelecionado === anoAtual && mesNum < mesAtual);

          return (
            <div 
              key={mesNum}
              className={`p-4 rounded-lg border ${
                balancete 
                  ? balancete.balancete_status === 'aprovado'
                    ? 'bg-green-50 border-green-200'
                    : balancete.balancete_status === 'fechado'
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-yellow-50 border-yellow-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {mes}
                </h3>
                {balancete && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    balancete.balancete_status === 'aprovado' ? 'bg-green-200 text-green-800' :
                    balancete.balancete_status === 'fechado' ? 'bg-blue-200 text-blue-800' :
                    'bg-yellow-200 text-yellow-800'
                  }`}>
                    {balancete.balancete_status === 'aprovado' && <Check className="h-3 w-3 inline mr-1" />}
                    {balancete.balancete_status === 'fechado' && <Lock className="h-3 w-3 inline mr-1" />}
                    {balancete.balancete_status}
                  </span>
                )}
              </div>

              {balancete ? (
                <>
                  <div className="space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Receitas:</span>
                      <span className="text-green-600 font-medium">
                        {formatCurrency(totalReceitasBalancete(balancete))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Despesas:</span>
                      <span className="text-red-600 font-medium">
                        {formatCurrency(totalDespesasBalancete(balancete))}
                      </span>
                    </div>
                    <div className="flex justify-between pt-1 border-t">
                      <span className="text-gray-700 font-medium">Saldo:</span>
                      <span className={`font-bold ${
                        saldoBalancete(balancete) >= 0 ? 'text-blue-600' : 'text-orange-600'
                      }`}>
                        {formatCurrency(saldoBalancete(balancete))}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => verDetalhes(balancete)}
                      className="flex-1 text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center justify-center gap-1"
                    >
                      <FileText className="h-3 w-3" />
                      Ver
                    </button>
                    {balancete.balancete_status === 'rascunho' && ['financeiro', 'administrador', 'sindico'].includes(user?.tipo) && (
                      <button
                        onClick={() => fecharBalancete(balancete.balancete_id || balancete.id)}
                        className="flex-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-1"
                      >
                        <Lock className="h-3 w-3" />
                        Fechar
                      </button>
                    )}
                    {balancete.balancete_status === 'fechado' && ['administrador', 'sindico'].includes(user?.tipo) && (
                      <button
                        onClick={() => aprovarBalancete(balancete.balancete_id || balancete.id)}
                        className="flex-1 text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center gap-1"
                      >
                        <Check className="h-3 w-3" />
                        Aprovar
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-3">Não gerado</p>
                  {mesPassado && ['financeiro', 'administrador'].includes(user?.tipo) && (
                    <button
                      onClick={() => gerarBalancete(mesNum, anoSelecionado)}
                      className="w-full text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center justify-center gap-1"
                    >
                      <Plus className="h-3 w-3" />
                      Gerar
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal de Detalhes */}
      {showDetalhes && balanceteDetalhado && (
        <div className="modal-overlay">
          <div className="modal-container" style={{maxWidth: '900px'}}>
            <div className="modal-header">
              <h3 className="modal-title flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Balancete - {meses[balanceteDetalhado.balancete_mes - 1]}/{balanceteDetalhado.balancete_ano}
              </h3>
              <button onClick={() => setShowDetalhes(false)} className="modal-close-btn">
                ×
              </button>
            </div>

            <div className="p-6">
              {/* Resumo */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-green-50 rounded">
                  <p className="text-sm text-green-600">Receitas</p>
                  <p className="text-xl font-bold text-green-700">
                    R$ {parseFloat(balanceteDetalhado.balancete_total_receitas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded">
                  <p className="text-sm text-red-600">Despesas</p>
                  <p className="text-xl font-bold text-red-700">
                    R$ {parseFloat(balanceteDetalhado.balancete_total_despesas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded">
                  <p className="text-sm text-blue-600">Saldo</p>
                  <p className="text-xl font-bold text-blue-700">
                    R$ {parseFloat(balanceteDetalhado.balancete_saldo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Detalhamento por Categoria */}
              {balanceteDetalhado.balancete_detalhes && (
                <div className="grid grid-cols-2 gap-6">
                  {/* Receitas */}
                  <div>
                    <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Receitas por Categoria
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(balanceteDetalhado.balancete_detalhes.receitas || {}).map(([categoria, valor]) => (
                        <div key={categoria} className="flex justify-between items-center p-2 bg-green-50 rounded">
                          <span className="text-sm">{categoria}</span>
                          <span className="font-medium text-green-700">
                            R$ {parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Despesas */}
                  <div>
                    <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <TrendingDown className="h-4 w-4" />
                      Despesas por Categoria
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(balanceteDetalhado.balancete_detalhes.despesas || {}).map(([categoria, valor]) => (
                        <div key={categoria} className="flex justify-between items-center p-2 bg-red-50 rounded">
                          <span className="text-sm">{categoria}</span>
                          <span className="font-medium text-red-700">
                            R$ {parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Botões de Ação */}
              <div className="mt-6 flex justify-end gap-2">
                <button
                  className="btn-base btn-secondary-custom flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Exportar PDF
                </button>
                <button
                  onClick={() => setShowDetalhes(false)}
                  className="btn-base btn-primary-custom"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Balancetes;

